import { useState, useEffect } from "react";
import { TbSortAscending } from "react-icons/tb";
import { TiArrowRight } from "react-icons/ti";
import {
  MdOutlineKeyboardDoubleArrowDown,
  MdOutlineKeyboardDoubleArrowUp,
} from "react-icons/md";
import { IoMan, IoWoman } from "react-icons/io5";
import { FiPlus } from "react-icons/fi";
import {
  FaCalendarCheck,
  FaFileInvoiceDollar,
  FaUser,
  FaUserMd,
} from "react-icons/fa";

import Cards from "../../components/Cards";
import "react-calendar/dist/Calendar.css";
import TopDoctorCard from "../../components/TopDoctorCard";
import Table from "../../components/Shared/Table";
import { useAuth } from "../../utils/AuthContext";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import axios from "axios";
import RevenueChart from "../../components/RevenueChart";
import PatientDonutChart from "../../components/PatientDonutChart";
import TechnicalSupportNotice from "../../components/TechnicalSupportNotice";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [doctorsData, setDoctorsData] = useState([]); 
  const [appointmentData, setAppointmentData] = useState({});
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPatients, setAllPatients] = useState([]);
  const [totalPatientsCount, setTotalPatientsCount] = useState(0);

  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const API_BASE_URL = "https://api.muktihospital.com";
  
  useEffect(() => {
      const loadDashboardData = async () => {
        setLoading(true);
        try {
          const api = axios.create({
            baseURL: API_BASE_URL,
            headers: { "x-api-key": API_KEY },
          });
          
          const countPromises = [
            api.get("/api/dashboard/appointment/count").then(res => res.data),
            api.get("/api/dashboard/patient/count").then(res => res.data),
            api.get("/api/dashboard/doctor/count").then(res => res.data),
          ];
          const allPatientsPromise = api.get("/api/patient").then(res => res.data);

          const today = new Date().toISOString().split("T")[0];
          const now = new Date();
          const year = now.getFullYear();
          const month = now.getMonth() + 1;

          const monthStartDate = `${year}-${String(month).padStart(2, '0')}-01`;
          const monthEndDate = new Date(year, month, 0).toISOString().split("T")[0];
          const yearStartDate = `${year}-01-01`;
          const yearEndDate = `${year}-12-31`;

          const revenueModules = [
            { url: "/api/appointment/fees" },
            { url: "/api/nicu/nicu-bills/bills" },
            { url: "/api/hospital/hospital-bills/bills" },
            { url: "/api/medicine-bill/bills" },
            { url: "/api/pathology-bill/bills" },
            { url: "/api/ambulance-call/calls" },
          ];

          const createRevenuePromise = (startDate, endDate) => 
            Promise.all(
              revenueModules.map(module =>
                api.get(`${module.url}/range?startDate=${startDate}&endDate=${endDate}`)
                  .then(response => parseFloat(response.data.totalAmount || response.data.totalFee || 0))
                  .catch(() => 0)
              )
            ).then(revenues => revenues.reduce((sum, amount) => sum + amount, 0));

          const revenuePromises = [
              createRevenuePromise(today, today),
              createRevenuePromise(monthStartDate, monthEndDate),
              createRevenuePromise(yearStartDate, yearEndDate),
          ];
          
          const [
            [appointmentsRes, patientsRes, doctorsRes],
            [todayRevenue, currentMonthRevenue, yearlyRevenue], allPatientsRes
          ] = await Promise.all([
              Promise.all(countPromises),
              Promise.all(revenuePromises),
              allPatientsPromise
          ]);

          setAppointmentData(appointmentsRes || {});
          setDoctorsData(doctorsRes?.recentDoctors || []);
          setAllPatients(allPatientsRes || []);
          setTotalPatientsCount(patientsRes?.totalPatients || 0);
          const staticCards = [
            {
              category: "Appointments",
              icon: <FaCalendarCheck className="text-blue-500 text-xl" />,
              total: appointmentsRes?.todayAppoinment || 0,
              status: "Today",
              cardListDetails: [
                { label: "Old Appointments", value: appointmentsRes?.oldAppoinment || 0 },
                { label: "Total Appointments", value: appointmentsRes?.totalAppoinment || 0 },
              ],
              link: "/all-appointments"
            },
            {
              category: "Total Patients",
              icon: <FaUser className="text-green-500 text-xl" />,
              total: patientsRes?.totalPatients || 0,
              cardListDetails: [
                { label: "New Patients", value: patientsRes?.todayPatients || 0 },
                { label: "Old Patients", value: patientsRes?.oldPatients || 0 },
              ],
              link: "/all-patients"
            },
            {
              category: "Doctors",
              icon: <FaUserMd className="text-red-500 text-xl" />,
              total: doctorsRes?.totalDoctors || 0,
              status: `New Today: ${doctorsRes?.todayDoctors || 0}`,
              cardListDetails: [
                { label: "Total Doctors", value: doctorsRes?.totalDoctors || 0 },
                { label: "Old Doctors", value: doctorsRes?.oldDoctors || 0 },
              ],
              link: "/all-doctors"
            }
          ];
          
          const revenueCard = {
            category: "Revenue",
            icon: <FaFileInvoiceDollar className="text-purple-500 text-xl" />,
            total: `৳${Math.round(currentMonthRevenue).toLocaleString()}`,
            status: "This Month",
            cardListDetails: [
              { label: "Today", value: `৳${Math.round(todayRevenue).toLocaleString()}` },
              { label: "This Year", value: `৳${Math.round(yearlyRevenue).toLocaleString()}` },
            ],
            link: "/accounts/revenue",
          };

          setDashboardData([...staticCards, revenueCard]);

        } catch (error) {
          console.error("Error loading dashboard data:", error);
        } finally {
          setLoading(false);
        }
      };
      loadDashboardData();
    }, []);

    // 5. Calculate counts for the summary display
  const maleCount = allPatients.filter(p => p.gender === 'male').length;
  const femaleCount = allPatients.filter(p => p.gender === 'female').length;
  const childCount = allPatients.filter(p => parseInt(p.age) < 18).length;

  // Calculate percentages
  const malePercentage = totalPatientsCount > 0 ? ((maleCount / totalPatientsCount) * 100) : 0;
  const femalePercentage = totalPatientsCount > 0 ? ((femaleCount / totalPatientsCount) * 100) : 0;
  const childPercentage = totalPatientsCount > 0 ? ((childCount / totalPatientsCount) * 100) : 0;
  // Show Skeleton until everything’s loaded
  if (loading) {
    return (
      <div className="p-4">
        <Skeleton count={10} />
      </div>
    );
  }

  return (
    <div className="md:py-4">
      {/* Top Title Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="font-semibold text-lg">
          Welcome back {user?.name|| "Admin"} 👋
        </h1>
        <div>
          <Link to="/add-appointment">
            <button className="py-2 px-4 bg-[#73CB64] hover:bg-green-700 text-white rounded-md flex items-center gap-2 text-sm">
              <TbSortAscending /> Add Appointment
            </button>
          </Link>
        </div>
      </div>

      {/* Cards */}
      <div className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {dashboardData.map((card, i) => (
            <Cards key={i} data={card} isEven={i % 2 === 0} />
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6">
        <TechnicalSupportNotice />
        {/* Patient Stats */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">
              Revenue Statistics{" "}
            </h2>
            <ul className="flex gap-2">
              {["All", "1M", "6M", "1Y"].map((t) => (
                <li
                  key={t}
                  className="w-9 py-1 text-center text-sm bg-gray-100 hover:bg-gray-200 rounded-sm cursor-pointer"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:p-4">
            {loading ? <Skeleton height={400} /> : <RevenueChart/>}
          </div>
        </div>
      </div>

      {/* Top Doctors & Breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6 mt-6">
        {/* Top Doctors */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="p-4 border-b">
            <h2 className="font-medium">Top Doctors</h2>
          </div>
          <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3  gap-4">
            {doctorsData.map((doc) => (
              // Add the isAdmin={true} prop here
              <TopDoctorCard key={doc.id} doctor={doc} isAdmin={true} />
            ))}
          </div>
          <div className="p-4 border-t">
            <Link to="/all-doctors">
              <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
                See All Doctors <TiArrowRight />
              </button>
            </Link>
          </div>
        </div>

        {/* Patient Breakdown */}
        <div className="bg-white shadow rounded-lg">
          <div className="flex justify-between flex-wrap gap-2 p-4 border-b">
            <h2 className="font-medium">Patient Breakdown</h2>
          </div>
          <div className="p-5">
            <PatientDonutChart patients={allPatients} />
            <div className="grid grid-cols-3 gap-1 md:gap-4 mt-4">
            {[
              { label: "Male Patient", icon: <IoMan className="text-blue-500" />, count: maleCount, percentage: malePercentage },
              { label: "Female Patient", icon: <IoWoman className="text-pink-500" />, count: femaleCount, percentage: femalePercentage },
              { label: "Child Patient", icon: <IoMan className="text-indigo-500" />, count: childCount, percentage: childPercentage }
            ].map((item) => {
              const isPositive = item.percentage > 33;
              const trendIcon = isPositive ? <MdOutlineKeyboardDoubleArrowUp /> : <MdOutlineKeyboardDoubleArrowDown />;
              const trendColor = isPositive ? 'text-green-600' : 'text-red-600';
              const trendBg = isPositive ? 'bg-green-100' : 'bg-red-100';

              return (
                <div key={item.label} className="text-center">
                  <h5 className="text-sm text-gray-600">{item.label}</h5>
                  <p className="flex items-center justify-center gap-1 text-base font-medium">
                    {item.icon} {item.count}
                  </p>
                  <span className={`inline-flex items-center gap-1 text-sm font-medium ${trendBg} ${trendColor} px-2 py-[2px] rounded-sm`}>
                    {trendIcon} {item.percentage.toFixed(2)}%
                  </span>
                </div>
              );
            })}
          </div>
          </div>
        </div>
      </div>

      {/* All Appointments Table */}
      <div className="mt-6 bg-white shadow rounded-lg">
        <div className="flex justify-between p-4 border-b">
          <h2 className="font-medium">All Appointments</h2>
          <Link to="/add-appointment">
            <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
              Add New <FiPlus />
            </button>
          </Link>
        </div>
        <div className="p-4">
          <Table
            columns={[
              { label: "Queue #", field: "queueNumber" },
              { label: "Name", field: "name" },
              { label: "Phone Number", field: "phone" },
              { label: "Serial No", field: "serial" },
              { label: "Date", field: "date" },
              { label: "Assign Dr.", field: "assignDr" },
              { label: "Status", field: "status" },
            ]}
            tableData={(appointmentData.recentAppointments || []).map(
              (appt) => {
                const date = new Date(appt.appointmentDate);
                const dateOnly = date.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                });
                return {
                  queueNumber: appt.id,
                  name: appt.patientName || "N/A",
                  phone: appt.mobileNumber || "N/A",
                  serial: appt.serialNumber || "N/A",
                  date: dateOnly,
                  assignDr: appt.doctorName || "N/A",
                  status: appt.status || "Scheduled",
                };
              }
            )}
          />
        </div>
        <div className="p-4 border-t">
          <Link to="/all-appointments">
            <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
              See All Appointments <TiArrowRight />
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
